/**
 * Advocate Blog `content_json` storage-shape helpers.
 *
 * The editor was migrated from BlockNote to the markdown-based
 * `RichTextEditor` (the same TipTap editor used by document generation).
 * `blog_posts.content_json` (jsonb) is untyped at the DB layer and the
 * publish guard (`empty_content`, see
 * `supabase/migrations/20260629062642_install_blog_runtime_functions.sql`)
 * only rejects `NULL` or `'[]'::jsonb` — so the new format is a
 * self-describing object that needs no migration:
 *
 *   `{ "format": "markdown", "markdown": "<the markdown>" }`
 *
 * When the markdown is empty/whitespace-only we write `[]` instead, so the
 * existing `empty_content` guard still fires for an empty post — this is
 * load-bearing, do not special-case around it.
 *
 * Three posts predate this migration and still carry a BlockNote block
 * *array* in `content_json`. Both shapes must keep working:
 *   - `Array`                          → legacy BlockNote content
 *   - `{ format: 'markdown', ... }`    → new markdown content
 *
 * This module is the ONE place that discriminates between the two shapes —
 * every reader/writer in the blog feature should go through it rather than
 * scattering `Array.isArray` checks.
 */

/** Legacy BlockNote document JSON — an array of block nodes, shape untyped
 *  at this layer (consumers narrow to `PartialBlock[]` only inside the
 *  legacy editor/renderer). */
export type LegacyBlockContent = unknown[];

/** New markdown-backed content shape. */
export interface MarkdownBlogContent {
  format: 'markdown';
  markdown: string;
}

/** Everything `blog_posts.content_json` can legitimately hold. */
export type BlogContentJson = LegacyBlockContent | MarkdownBlogContent;

export function isMarkdownBlogContent(
  value: unknown
): value is MarkdownBlogContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as { format?: unknown }).format === 'markdown' &&
    typeof (value as { markdown?: unknown }).markdown === 'string'
  );
}

export function isLegacyBlockContent(
  value: unknown
): value is LegacyBlockContent {
  return Array.isArray(value);
}

/**
 * Build the `content_json` value to persist for a given markdown string.
 * Empty/whitespace-only markdown maps to `[]` so the `empty_content`
 * publish guard (which rejects only `NULL`/`[]`) keeps working unchanged.
 */
export function serializeMarkdownContent(markdown: string): BlogContentJson {
  if (markdown.trim().length === 0) return [];
  return { format: 'markdown', markdown };
}

/**
 * Recover the markdown string to seed the editor with. Malformed/unknown
 * input (including a legacy array) yields `''` — callers that need
 * best-effort legacy conversion use {@link legacyBlockContentToMarkdown}
 * explicitly.
 */
export function markdownFromContent(content: unknown): string {
  if (isMarkdownBlogContent(content)) return content.markdown;
  return '';
}

interface LegacyInlineNode {
  type?: string;
  text?: string;
  styles?: Record<string, unknown>;
  href?: string;
  content?: LegacyInlineNode[];
}

interface LegacyBlockNode {
  type?: string;
  props?: Record<string, unknown>;
  content?: LegacyInlineNode[];
  children?: LegacyBlockNode[];
}

function legacyInlineToMarkdown(nodes: LegacyInlineNode[] | undefined): string {
  if (!Array.isArray(nodes)) return '';
  return nodes
    .map((node) => {
      if (node.type === 'link' && Array.isArray(node.content)) {
        const label = legacyInlineToMarkdown(node.content);
        const href = typeof node.href === 'string' ? node.href : '';
        return `[${label}](${href})`;
      }
      if (node.type === 'text') {
        let text = node.text ?? '';
        const styles = node.styles ?? {};
        if (styles['code']) text = `\`${text}\``;
        if (styles['bold']) text = `**${text}**`;
        if (styles['italic']) text = `_${text}_`;
        return text;
      }
      return '';
    })
    .join('');
}

function legacyBlockToMarkdownLines(block: LegacyBlockNode): string[] {
  const inline = legacyInlineToMarkdown(block.content);
  switch (block.type) {
    case 'heading': {
      const level = Number(block.props?.['level']) || 1;
      const hashes = '#'.repeat(Math.min(Math.max(level, 1), 3));
      return [`${hashes} ${inline}`];
    }
    case 'bulletListItem':
      return [`- ${inline}`];
    case 'numberedListItem':
      return [`1. ${inline}`];
    case 'quote':
      return [`> ${inline}`];
    case 'codeBlock':
      return ['```', inline, '```'];
    case 'image': {
      const url =
        typeof block.props?.['url'] === 'string' ? block.props['url'] : '';
      const caption =
        typeof block.props?.['caption'] === 'string'
          ? block.props['caption']
          : '';
      return url ? [`![${caption}](${url})`] : [];
    }
    case 'paragraph':
    default:
      return inline ? [inline] : [];
  }
}

/**
 * Best-effort, one-way conversion of a legacy BlockNote document into
 * markdown — used ONLY to seed the new editor when an author opens a
 * pre-migration post. Not fidelity-preserving (nested list/child blocks
 * are flattened); good enough that editing an old post doesn't silently
 * discard its content.
 */
export function legacyBlockContentToMarkdown(blocks: unknown): string {
  if (!Array.isArray(blocks)) return '';
  const lines: string[] = [];
  for (const raw of blocks as LegacyBlockNode[]) {
    if (!raw || typeof raw !== 'object') continue;
    lines.push(...legacyBlockToMarkdownLines(raw));
    if (Array.isArray(raw.children) && raw.children.length > 0) {
      lines.push(legacyBlockContentToMarkdown(raw.children));
    }
  }
  return lines.filter((l) => l.length > 0).join('\n\n');
}

/**
 * Plain-text excerpt (no markdown/markup) from either content shape, used
 * for cards and meta descriptions. Mirrors the legacy behaviour of walking
 * BlockNote paragraphs/inline text, plus a markdown-strip path for the new
 * shape.
 */
export function blogContentExcerpt(content: unknown, maxChars = 200): string {
  if (isMarkdownBlogContent(content)) {
    const plain = content.markdown
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/[*_`>~]/g, '')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\s+/g, ' ')
      .trim();
    return plain.length <= maxChars
      ? plain
      : `${plain.slice(0, maxChars - 1).trimEnd()}…`;
  }
  if (!Array.isArray(content)) return '';
  const parts: string[] = [];
  let total = 0;
  for (const block of content as LegacyBlockNode[]) {
    if (total >= maxChars) break;
    const inline = block?.content;
    if (!Array.isArray(inline)) continue;
    for (const node of inline) {
      if (total >= maxChars) break;
      const text = node?.text;
      if (typeof text === 'string' && text.length > 0) {
        parts.push(text);
        total += text.length;
      }
    }
    if (total < maxChars && parts.length > 0) {
      parts.push(' ');
      total += 1;
    }
  }
  const joined = parts.join('').replace(/\s+/g, ' ').trim();
  return joined.length <= maxChars
    ? joined
    : `${joined.slice(0, maxChars - 1).trimEnd()}…`;
}
